import React, { createContext, useContext, useState, useEffect } from 'react';
import storage from '../utils/storage';

const ThemeContext = createContext();

export const THEMES = ['dark', 'light', 'white'];

// Per-theme CSS variables (consumed by src/themes.css + inline `var(--theme-bg)` usages)
const VARS = {
  dark: {
    '--theme-bg': 'linear-gradient(to bottom right, #111827, #000000, #111827)',
    '--theme-bg-solid': '#000000',
    '--theme-nav-bg': 'rgba(0, 0, 0, 0.9)',
    '--theme-card-bg': 'rgba(255, 255, 255, 0.05)',
    '--theme-text': '#ffffff',
    '--theme-text-secondary': 'rgba(255, 255, 255, 0.6)',
    '--theme-border': 'rgba(255, 255, 255, 0.1)',
    '--theme-accent': '#d946ef',
  },
  light: {
    '--theme-bg': 'linear-gradient(180deg, #fff1f7 0%, #fce7f3 45%, #f5d0fe 100%)',
    '--theme-bg-solid': '#fdf2f8',
    '--theme-nav-bg': 'rgba(255, 241, 247, 0.92)',
    '--theme-card-bg': 'rgba(190, 24, 93, 0.06)',
    '--theme-text': '#2a0a2e',
    '--theme-text-secondary': 'rgba(42, 10, 46, 0.65)',
    '--theme-border': 'rgba(190, 24, 93, 0.16)',
    '--theme-accent': '#c026d3',
  },
  white: {
    '--theme-bg': 'linear-gradient(180deg, #ffffff 0%, #f7f7fa 100%)',
    '--theme-bg-solid': '#ffffff',
    '--theme-nav-bg': 'rgba(255, 255, 255, 0.94)',
    '--theme-card-bg': 'rgba(17, 17, 24, 0.045)',
    '--theme-text': '#111118',
    '--theme-text-secondary': 'rgba(17, 17, 24, 0.62)',
    '--theme-border': 'rgba(17, 17, 24, 0.11)',
    '--theme-accent': '#7c3aed',
  },
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState('light'); // default = rose light | 'dark' | 'white'
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTheme();
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    THEMES.forEach((t) => root.classList.remove(t));
    root.classList.remove('theme-light-any');
    root.classList.add(theme);
    if (theme !== 'dark') root.classList.add('theme-light-any');
    root.style.colorScheme = theme === 'dark' ? 'dark' : 'light';
    Object.entries(VARS[theme] || VARS.dark).forEach(([k, v]) => document.body.style.setProperty(k, v));
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', theme === 'dark' ? '#000000' : theme === 'light' ? '#fff1f7' : '#ffffff');
  }, [theme]);

  const loadTheme = async () => {
    try {
      const savedTheme = await storage.get('app_theme');
      if (savedTheme && THEMES.includes(savedTheme)) {
        setThemeState(savedTheme);
      }
    } catch (error) {
      console.error('Error loading theme:', error);
    } finally {
      setLoading(false);
    }
  };

  const setTheme = async (newTheme) => {
    if (!THEMES.includes(newTheme)) return;
    try {
      setThemeState(newTheme);
      await storage.set('app_theme', newTheme);
    } catch (error) {
      console.error('Error saving theme:', error);
    }
  };

  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  };

  const value = {
    theme,
    setTheme,
    toggleTheme,
    isDark: theme === 'dark',
    isLight: theme !== 'dark',
    isWhite: theme === 'white',
    loading
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
